import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Search,
  Restaurant,
  LocationOn,
  CheckCircle,
  Lock,
} from '@mui/icons-material';
import axios from 'axios';
import { ENDPOINTS } from '../../config';

const VenueSearch = () => {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const errorRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    document.title = 'Find Your Venue | Piatto';
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  // Move focus to results once they arrive
  useEffect(() => {
    if (searchPerformed && resultsRef.current) {
      resultsRef.current.focus();
    }
  }, [searchPerformed]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a venue name to search');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setSearchPerformed(false);

      const response = await axios.post(
        ENDPOINTS.searchVenueForClaim,
        { venueName: searchQuery }
      );

      setVenues(response.data.venues || []);
      setSearchPerformed(true);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search for venues. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestClaimEmail = async (venue) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      await axios.post(
        ENDPOINTS.resendClaimEmail,
        { venueId: venue.id }
      );

      setSuccessMessage(
        `Claim email sent for ${venue.name}. Please check the business email inbox for the claim link.`
      );
    } catch (err) {
      console.error('Error sending claim email:', err);
      setError(
        err.response?.data?.error ||
          'Failed to send claim email. Please contact support.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Restaurant sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} aria-hidden="true" />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Claim Your Venue
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Can't find your claim email? Search for your venue below
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              role="alert"
              ref={errorRef}
              tabIndex={-1}
            >
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }} role="status">
              {successMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <TextField
              fullWidth
              label="Search for your venue"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter venue name (e.g., 'The Coffee Shop')"
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading}
              aria-busy={loading}
              aria-label="Search for venues"
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Search'}
            </Button>
          </Box>

          {/* Live region announces result count to screen readers */}
          <Box
            aria-live="polite"
            aria-atomic="true"
            ref={resultsRef}
            tabIndex={-1}
            sx={{ outline: 'none' }}
          >
            {searchPerformed && venues.length === 0 && (
              <Alert severity="info">
                No venues found matching "{searchQuery}". Try a different search term
                or contact support at hello@piatto.com.au
              </Alert>
            )}

            {venues.length > 0 && (
              <Box>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                  Found {venues.length} venue{venues.length !== 1 ? 's' : ''}:
                </Typography>

                <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                  {venues.map((venue, index) => (
                    <React.Fragment key={venue.id}>
                      {index > 0 && <Divider />}
                      <ListItem
                        sx={{
                          py: 2,
                          px: 3,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {venue.name}
                              </Typography>
                              {venue.alreadyClaimed && (
                                <Chip
                                  icon={<Lock aria-hidden="true" />}
                                  label="Already Claimed"
                                  size="small"
                                  color="default"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <LocationOn fontSize="small" aria-hidden="true" />
                                <Typography variant="body2">
                                  {venue.address}
                                  {venue.city && `, ${venue.city}`}
                                  {venue.state && ` ${venue.state}`}
                                </Typography>
                              </Box>
                              {venue.phone && (
                                <Typography variant="body2" color="text.secondary">
                                  {venue.phone}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Box sx={{ ml: 2 }}>
                          {venue.alreadyClaimed ? (
                            <Chip
                              icon={<CheckCircle aria-hidden="true" />}
                              label="Claimed"
                              color="success"
                              variant="outlined"
                            />
                          ) : (
                            <Button
                              variant="contained"
                              onClick={() => handleRequestClaimEmail(venue)}
                              disabled={loading}
                              aria-label={`Send claim email for ${venue.name}`}
                            >
                              Send Claim Email
                            </Button>
                          )}
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              How it works:
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              1. Search for your venue by name
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              2. Click "Send Claim Email" to receive a claim link
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              3. Check your business email inbox for the claim link
            </Typography>
            <Typography variant="body2" color="text.secondary">
              4. Click the link to verify and claim your venue
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help?{' '}
          <a
            href="mailto:hello@piatto.com.au"
            style={{ color: '#B91C1C' }}
            aria-label="Contact our support team by email"
          >
            Contact our support team
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default VenueSearch;
